import Section from "components/layout/section";
import { Trans, useTranslation } from "react-i18next";
import PageTitle from "features/navigation/pageTitle";
import RightSidebar from "@/components/sidebar/right/RightSidebar";
import Button from "@/components/inputs/button";
import { ReactComponent as SignUpIcon } from "assets/icons/group_add.svg";
import { ReactComponent as LoginIcon } from "assets/icons/login.svg";
import { ReactComponent as StoragesIcon } from "assets/icons/home_storage.svg";
import { ReactComponent as CategoryIcon } from "assets/icons/category.svg";
import { ReactComponent as BoxIcon } from "assets/icons/box.svg";
import { ReactComponent as ActionsIcon } from "assets/icons/actions.svg";
import { ReactComponent as GroupIcon } from "assets/icons/group.svg";
import { ReactComponent as AccountIcon } from "assets/icons/account_circle.svg";
import { ReactComponent as TransactionsIcon } from "assets/icons/description.svg";
import { ReactComponent as ListIcon } from "assets/icons/format_list_bulleted.svg";
import { ReactComponent as SendIcon } from "assets/icons/send.svg";
import { useHideMainSheet } from "@/components/layout/layoutChrome";
import useAuth from "@/hooks/useAuth";

const CONCEPTS = [
    { titleKey: "homeConceptStoragesTitle", descriptionKey: "homeConceptStoragesDescription", Icon: StoragesIcon, iconClass: "fill-blue-700", badgeClass: "!bg-blue-100" },
    { titleKey: "homeConceptCategoriesTitle", descriptionKey: "homeConceptCategoriesDescription", Icon: CategoryIcon, iconClass: "fill-violet-700", badgeClass: "!bg-violet-100" },
    { titleKey: "homeConceptProductsTitle", descriptionKey: "homeConceptProductsDescription", Icon: BoxIcon, iconClass: "fill-orange-700", badgeClass: "!bg-orange-100" },
    { titleKey: "homeConceptStockTitle", descriptionKey: "homeConceptStockDescription", Icon: ActionsIcon, iconClass: "fill-emerald-700", badgeClass: "!bg-emerald-100" },
    { titleKey: "homeConceptTransactionsTitle", descriptionKey: "homeConceptTransactionsDescription", Icon: TransactionsIcon, iconClass: "fill-cyan-700", badgeClass: "!bg-cyan-100" },
] as const;

const SHARING_ORBIT_USERS = 5;

const sharingOrbitPosition = (index: number) => {
    const angle = (Math.PI * 2 * index) / SHARING_ORBIT_USERS - Math.PI / 2;
    const radius = 42;
    return {
        left: `${50 + Math.cos(angle) * radius}%`,
        top: `${50 + Math.sin(angle) * radius}%`,
    };
};

const SharingOrbit = () => (
    <div aria-hidden="true" className="relative h-72 w-72 shrink-0">
        <div className="absolute left-1/2 top-1/2 h-[84%] w-[84%] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-rose-200 bg-rose-50" />
        {Array.from({ length: SHARING_ORBIT_USERS }, (_, index) => (
            <span
                key={index}
                className="absolute flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md"
                style={sharingOrbitPosition(index)}
            >
                <AccountIcon className="h-8 w-8 fill-rose-700" />
            </span>
        ))}
        <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full !bg-blue-100 shadow-md">
            <StoragesIcon className="h-10 w-10 fill-blue-700" />
        </span>
    </div>
);

const HomePage = () => {
    const { t } = useTranslation();
    const { tokenIsValid } = useAuth();
    const isLoggedIn = tokenIsValid === true;
    useHideMainSheet();

    return (
        <>
            <PageTitle title={t("titleHome")} />
            <Section design="card-centered" className={`border-none !py-32`}>
                <h1 className="w-full max-w-full min-w-0 whitespace-normal break-words text-4xl min-[380px]:text-5xl">
                    <Trans
                        i18nKey="homeHeadline"
                        components={{ highlight: <span className="rounded-sm !bg-yellow-200 px-2 !text-black" /> }}
                    />
                    <br />
                    {t("homeHeadlineAccent")}
                </h1>
                <span className="flex w-full max-w-full flex-row flex-wrap justify-center gap-4 mt-4">
                    {isLoggedIn ? (
                        <Button size="large" design="primary" to="/storages">
                            <StoragesIcon />
                            <span>{t("buttonGetStarted")}</span>
                        </Button>
                    ) : tokenIsValid === false ? (
                        <>
                            <Button size="large" design="primary" to="/sign-up">
                                <SignUpIcon />
                                <span>{t("buttonTryOrdnatFree")}</span>
                            </Button>
                            <Button size="large" design="white" to="/Login">
                                <LoginIcon />
                                <span>{t("titleLogin")}</span>
                            </Button>
                        </>
                    ) : null}
                </span>
            </Section>
            <Section design="card-grid-large">
                {CONCEPTS.map(({ titleKey, descriptionKey, Icon, iconClass, badgeClass }) => (
                    <Section key={titleKey} design="panel">
                        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-4 py-2">
                            <span className={`inline-flex shrink-0 rounded-md p-2 ${badgeClass}`}>
                                <Icon className={`h-12 w-12 ${iconClass}`} />
                            </span>
                            <div className="flex min-w-0 flex-col gap-y-2">
                                <h2 className="whitespace-normal">{t(titleKey)}</h2>
                                <p className={`text-gray-700`}>{t(descriptionKey)}</p>
                            </div>
                        </div>
                    </Section>
                ))}
            </Section>
            <Section design="panel" className="items-center">
                <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-8 py-6 lg:flex-row lg:gap-14">
                    <SharingOrbit />
                    <div className="flex w-full min-w-0 flex-col gap-y-2 lg:w-auto lg:flex-1">
                        <h2 className="whitespace-normal">{t("homeConceptSharingTitle")}</h2>
                        <p className="text-gray-700">
                            {t("homeConceptSharingDescription")} {t("homeSharingTeamLine")}
                        </p>
                    </div>
                </div>
            </Section>
            <Section design="card-centered" className={`border-none !py-32`}>
                <h1 className="w-full max-w-full min-w-0 whitespace-normal break-words text-4xl min-[380px]:text-5xl">{t("homeQuestionsTitle")}</h1>
                <span className="flex w-full max-w-full flex-row flex-wrap justify-center gap-4 mt-4">
                    <Button size="large" design="primary" to="/faq">
                        <ListIcon />
                        <span>{t("buttonViewFaq")}</span>
                    </Button>
                    <Button size="large" design="white" to="/support">
                        <SendIcon />
                        <span>{t("buttonContactSupport")}</span>
                    </Button>
                </span>
            </Section>
            <RightSidebar />
        </>
    );
};

export default HomePage;
